import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import Immutable from 'immutable';

import {
  UI_OPERATION_STATUS_SUCCESS,
  UI_OPERATION_STATUS_ERROR,
  UI_OPERATION_STATUS_REQUEST,
  setOperationStatus,
} from '@uncinc/react-drupal-core';

import { Button, Container } from '@uncinc/uncinc-react-kitchen-sink';

import useCartProduct from './helpers/useCartProduct';
import useProductAccessCheck from './helpers/useProductAccessCheck';
import {
  getProductType,
  cartHasElearningProduct,
  type ProductType,
} from './helpers/digitalProductHelpers';

import { commerceCartFinishOrder } from '../../../store/actions/Commerce';
import {
  createOrUpdateNewsletterContactSubscription,
  clearPageInformation,
  setPageInformation,
  setPageViewSent,
  getNewsletterContactPropertyOptions,
  setPrevOrderInfo,
} from '../../../store/actions/UI';
import CustomFormikWrapper from '../../../components/CustomFormikWrapper';
import PersonalInformationSection from './Components/PersonalInformationSection';
import BillingAddressSection from './Components/BillingAddressSection';
import ParticipantsBanner from './Components/ParticipantsBanner';
import OrderForSomeOneElseSection from './Components/OrderForSomeOneElse';
import ProductOverviewSection from './Components/ProductOverviewSection';
import ProductAccessBlockedBanner from './Components/ProductAccessBlockedBanner';
import checkoutSubmit from './helpers/checkoutSubmit';
import createSignupSchema from './helpers/checkoutValidation';
import selectAddress from './helpers/addressHelpers';
import CartSummaryV2 from '../CartSummaryV2';
import DiscountCodeSection from '../DiscountCodeSection';
import FormErrorSummary from '../../../components/FormErrorSummary';

import { formatCartItemsForAnalytics } from '../../../helpers/commerce';
import { getPhysicalCheckoutInitialValues } from './helpers/checkoutInitialValues';

import type {
  CheckoutFormValues,
  AnalyticsItem,
  CartData,
  AddressData,
} from './types/checkout.types';

import '../style.scss';
import './style.scss';
import { DEFAULT_LANGUAGE } from '../../../config/constants';

/**
 * Checkout page component
 *
 * Manages the checkout form flow including:
 * - Personal information collection
 * - Billing address entry
 * - Delivery address selection
 * - Form validation and submission
 * - Redux state management
 *
 * @component
 */
/**
 * Checkout phase tracking for sequential API calls
 * - 'idle': No checkout in progress
 * - 'billing_submitted': Billing info submitted, waiting for success
 * - 'finishing_order': Billing saved, now finishing order
 * - 'complete': Order finalized successfully
 */
type CheckoutPhase =
  | 'idle'
  | 'billing_submitted'
  | 'finishing_order'
  | 'complete';

/**
 * Get page title translation key based on product type and price
 * @param productType - The type of product being purchased
 * @param isFree - Whether the product is free (price is 0)
 * @returns Translation key for the page title
 */
const getPageTitleKey = (productType: ProductType, isFree: boolean): string => {
  // Free e-learning products have a special registration title
  if (productType === 'e_learning' && isFree) {
    return 'checkout.page_title_elearning_free';
  }

  const titleKeys: Record<ProductType, string> = {
    e_learning: 'checkout.page_title_elearning',
    digital_tool: 'checkout.page_title_digital_tool',
    physical: 'checkout.page_title_physical',
    unknown: 'checkout.page_title_default',
  };
  return titleKeys[productType];
};

const CheckoutPageV2 = (): React.ReactElement | null => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [newsletter, setNewsletter] = useState<boolean>(false);

  // Track checkout phase to prevent race condition between billing and order finalization
  const [checkoutPhase, setCheckoutPhase] = useState<CheckoutPhase>('idle');

  // TODO: Replace 'any' with proper FormikProps type when available
  const formRef = useRef<any>(null);
  const [selectedBillingAddressIndex, setSelectedBillingAddressIndex] =
    useState<number | null>(null);
  const blockedBannerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  // Use granular selectors instead of converting entire state to JS
  const loggedIn = useSelector((state: any) => state.getIn(['login', 'loggedIn'], false));
  const uid = useSelector((state: any) => state.getIn(['login', 'uid'])) as string;
  const checkoutStatus = useSelector((state: any) => state.getIn(['ui', 'commerce', 'operations', 'cart_checkout', 'status']));
  const orderNumber = useSelector((state: any) => state.getIn(['uiInternal', 'prevOrderNumber'], ''));

  const isLoading = checkoutStatus === UI_OPERATION_STATUS_REQUEST;

  // Select cart data with memoization
  const cartContent = useSelector((state: any) => state.getIn(['commerce', 'cart', 'content']));
  const cartProducts = useSelector((state: any) => state.getIn(['commerce', 'cart', 'products'], Immutable.List()));

  const cart: CartData = useMemo(() => {
    const content = cartContent ? cartContent.toJS() : null;
    const products = cartProducts.toJS();
    return { content, products } as CartData;
  }, [cartContent, cartProducts]);

  // Determine product type for dynamic page title
  const productType = useMemo(
    () => getProductType(cartProducts),
    [cartProducts],
  );

  const isDigitalProduct =
    productType === 'e_learning' || productType === 'digital_tool';

  // Check if cart contains specifically e-learning (not digital tools)
  // Used for features that only apply to e-learning, like "order for someone else"
  const isElearningProduct = useMemo(
    () => cartHasElearningProduct(cartProducts),
    [cartProducts],
  );

  // Select user data
  const userMail = useSelector((state: any) => state.getIn(['user', uid, 'mail', 0, 'value'], ''));
  const userDebtorNumber = useSelector((state: any) => state.getIn(['user', uid, 'field_external_relation_number', 0, 'value'], ''));
  const userDebtor = useSelector((state: any) => state.getIn(['user', uid, 'debtors', userDebtorNumber], Immutable.Map()));
  const userProfile = useSelector((state: any) => state.getIn(['user', uid, 'profile'], Immutable.Map()));
  const userAddresses = useSelector((state: any) => state.getIn(['user', uid, 'profile', 'address'], Immutable.List()));
  const spotlerUserContact = useSelector((state: any) => state.getIn(['uiInternal', 'newsletter', 'contact']));

  const totalPrice =
    cart?.content?.attributes?.order_total?.subtotal?.number || 0;

  // Memoize analytics items calculation
  const analyticsItems: AnalyticsItem[] = useMemo(
    () => formatCartItemsForAnalytics(cartProducts) || [],
    [cartProducts],
  );

  const customerEmail = userMail;
  const debtor = userDebtor.toJS ? userDebtor.toJS() : userDebtor;

  const billingAddress = (userProfile
    .getIn(['field_billing_address', 0])
    ?.toJS?.() ?? {}) as AddressData;
  const firstName = userProfile.getIn(['field_first_name', 0, 'value'], '');
  const lastName = [
    userProfile.getIn(['field_infix_name', 0, 'value'], ''),
    userProfile.getIn(['field_last_name', 0, 'value'], ''),
  ]
    .filter(Boolean)
    .join(' ');
  const phoneNumber = userProfile.getIn(['field_telephone', 0, 'value'], '');
  const defaultAddressIndexStr = userProfile.getIn([
    'field_default_address_index',
    0,
    'value',
  ]);
  const defaultAddressIndex = defaultAddressIndexStr
    ? Number(defaultAddressIndexStr)
    : null;
  const defaultBillingIndexStr = userProfile.getIn([
    'field_default_billing_index',
    0,
    'value',
  ]);
  const defaultBillingIndex = defaultBillingIndexStr
    ? Number(defaultBillingIndexStr)
    : null;
  const productTitle = cart?.products?.[0]?.attributes?.title ?? '';

  const billingEmail =
    userProfile.getIn(['field_billing_email_address', 0, 'value']) ||
    debtor.invoiceEmail ||
    cart.content?.attributes?.field_invoice_email ||
    customerEmail ||
    '';

  const billingReference =
    userProfile.getIn(['field_billing_reference', 0, 'value']) ||
    cart.content?.attributes?.field_customer_reference ||
    '';

  // Get cart product data and quantity state from custom hook
  const { cartProductQuantity, cartProductPrice } = useCartProduct();
  const isPaidProduct = Number(cartProductPrice) !== 0;
  const isPaidElearning = productType === 'e_learning' && isPaidProduct;
  const hasMultipleProductsInCart = Number(cartProductQuantity) >= 2;

  // Product access check — timing varies per product type:
  // digital_tool: immediate (sync), free e-learning: on mount, paid e-learning: on submit.
  const {
    checkAccess,
    isChecking,
    result: accessResult,
    resetResult,
  } = useProductAccessCheck(cartProducts, isPaidProduct);

  // Determine page title based on product type and price
  const pageTitleKey = getPageTitleKey(productType, !isPaidProduct);
  const PAGE_TITLE = `${t(pageTitleKey)} | VeiligheidNL`;

  // Memoize addresses to prevent unnecessary re-renders
  const addressesRaw = useMemo(
    () => (userAddresses.toJS ? userAddresses.toJS() : userAddresses),
    [userAddresses],
  );

  useEffect(() => {
    if (
      defaultBillingIndex !== null &&
      selectedBillingAddressIndex === null &&
      formRef &&
      Array.isArray(addressesRaw) &&
      addressesRaw.length > 0
    ) {
      setSelectedBillingAddressIndex(defaultBillingIndex);
      selectAddress(formRef, addressesRaw[defaultBillingIndex], 'billing_');
    }
  }, [defaultBillingIndex, selectedBillingAddressIndex, addressesRaw, formRef]);

  useEffect(() => {
    if (loggedIn === false) {
      history.replace('/cart/checkoutv2/login-or-register');
      return;
    }

    // Handle errors - reset checkout phase to allow retry
    if (
      checkoutPhase !== 'idle' &&
      checkoutPhase !== 'complete' &&
      checkoutStatus === UI_OPERATION_STATUS_ERROR
    ) {
      setCheckoutPhase('idle');
      return;
    }

    // Phase 1: Billing info submitted, waiting for success
    // When billing info is saved successfully, proceed to finish the order
    if (
      checkoutPhase === 'billing_submitted' &&
      checkoutStatus === UI_OPERATION_STATUS_SUCCESS
    ) {
      setCheckoutPhase('finishing_order');
      // Now that billing info is saved, finalize the order
      // @ts-expect-error - Redux thunk dispatch returns function, not AnyAction
      dispatch(commerceCartFinishOrder());
      return;
    }

    // Phase 2: Order finalization complete
    // When checkout is successful after finishing order, redirect
    if (
      checkoutPhase === 'finishing_order' &&
      checkoutStatus === UI_OPERATION_STATUS_SUCCESS
    ) {
      setCheckoutPhase('complete');

      // Send GA4 purchase event (matching V1 checkout pattern)
      if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'purchase',
          ecommerce: {
            transaction_id: orderNumber,
            currency: 'EUR',
            value: totalPrice,
            tax: 0,
            shipping: 0,
            items: analyticsItems,
          },
        });
      }

      dispatch(
        setPrevOrderInfo({
          isElearning: isElearningProduct,
          isFreeProduct: !isPaidProduct,
          productType,
        }),
      );
      history.push('/cart/checkout/success');
      dispatch(setOperationStatus('commerce', 'cart_checkout', null, null));
    }
  }, [
    dispatch,
    history,
    checkoutStatus,
    loggedIn,
    checkoutPhase,
    orderNumber,
    totalPrice,
    analyticsItems,
  ]);

  useEffect(() => {
    dispatch(clearPageInformation());
    dispatch(setPageInformation(PAGE_TITLE));
    dispatch(setPageViewSent(false));
  }, [dispatch]);

  const initialValues: CheckoutFormValues = getPhysicalCheckoutInitialValues({
    customerEmail,
    firstName,
    lastName,
    phoneNumber,
    billingEmail,
    billingReference,
    billingAddress,
    debtor,
    orderForSomeOneElse: false, // Updated imperatively via formRef.setFieldValue
    cart,
    defaultAddressIndex,
    addresses: addressesRaw,
  });

  const onSubmit = async (
    formValues: CheckoutFormValues,
    form: any,
  ): Promise<void> => {
    // A second submit while the first is still running would re-enter the
    // phase machine and place the order twice.
    if (checkoutPhase !== 'idle') {
      return;
    }

    // Paid e-learning: check enrollment on submit (needs form values).
    // Digital tool and free e-learning are already checked before mount/render.
    if (productType === 'e_learning' && isPaidProduct) {
      const submitResult = await checkAccess(
        formValues.order_someone_else,
        Number(cartProductQuantity),
      );

      if (submitResult.isBlocked) {
        form.setSubmitting(false);
        blockedBannerRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        return;
      }
    }

    setNewsletter(true);

    // Mark that we're in the billing submission phase
    // The useEffect will handle calling commerceCartFinishOrder after billing succeeds
    setCheckoutPhase('billing_submitted');

    // First, submit the form data (billing/shipping addresses)
    // commerceCartFinishOrder will be called by useEffect after this succeeds
    checkoutSubmit(formValues, form, {
      totalPrice,
      analyticsItems,
      dispatch,
      sendBillingInfo: isPaidProduct,
      sendShippingInfo: false,
    });

    // Send analytics event for order finalization
    window.dataLayer.push({
      event: 'webshop.order.finalize',
    });

    // Send per-product analytics events
    if (cart?.products && Array.isArray(cart.products)) {
      cart.products.forEach((product: any) => {
        const productName = product.attributes?.title || '';
        const productPrice = product.attributes?.price || 0;
        const productQuantity = product.attributes?.quantity || 1;
        const productSku = product.attributes?.sku || '';

        window.dataLayer.push({
          event: 'webshop.product.order',
          productName,
          productPrice,
          productQuantity,
          productSku,
        });
      });
    }

    // Handle newsletter subscription
    if (newsletter && spotlerUserContact) {
      const extraProfileData = {
        email: customerEmail,
        firstName,
        lastName,
        ...spotlerUserContact,
      };

      // @ts-expect-error - Redux thunk dispatch returns function, not AnyAction
      dispatch(createOrUpdateNewsletterContactSubscription(extraProfileData));
    }

    // Send newsletter analytics event
    window.dataLayer.push({
      event: 'webshop.order.subscribe_to_newsletter',
      value: newsletter,
    });
  };

  // Try to fetch our Spotler account if we are logged in.
  useEffect(() => {
    if (!spotlerUserContact) {
      // @ts-expect-error - Redux thunk dispatch returns function, not AnyAction
      dispatch(getNewsletterContactPropertyOptions());
    }
  }, []);

  // Prevent form from being initialized before we have all the required data.
  if (!customerEmail || !cart || !cart.content) {
    return null;
  }

  return (
    <>
      <Helmet htmlAttributes={{ lang: DEFAULT_LANGUAGE }}>
        <title>{PAGE_TITLE}</title>
      </Helmet>
      <div className="CheckoutV2__hero">
        <Container>
          <div className="row">
            <div className="col-12 col-lg-7">
              <div className="CheckoutV2__hero__title">
                <h1>{t(pageTitleKey)}</h1>
              </div>
            </div>
          </div>
        </Container>
      </div>
      <Container className="signUpForm signUpForm__elearning">
        {checkoutStatus === UI_OPERATION_STATUS_ERROR && (
          <div className="Form__error">{t('checkout.error_message')}</div>
        )}

        {!isChecking && accessResult?.isBlocked && !isPaidElearning && (
          <ProductAccessBlockedBanner
            productType={accessResult.productType}
            reason={accessResult.reason}
          />
        )}

        {(isPaidElearning || (!isChecking && !accessResult?.isBlocked)) && (
          <CustomFormikWrapper
            onSubmit={onSubmit}
            initialValues={initialValues}
            validationSchema={createSignupSchema(t, {
              requiresBillingInfo: isPaidProduct,
            })}
            innerRef={formRef}
            enableReinitialize
          >
            {(formikProps: {
              values: CheckoutFormValues;
              isSubmitting: boolean;
              isValid: boolean;
              errors: Record<string, string>;
              submitCount: number;
            }) => (
              <div className="Cart__container">
                <div className="Cart__content">
                  <FormErrorSummary
                    errors={formikProps.errors}
                    visible={
                      formikProps.submitCount > 0 && !formikProps.isValid
                    }
                  />
                  <p className="CheckoutV2__intro">
                    {productType === 'digital_tool'
                      ? t('checkout.digital_tool_intro')
                      : t(
                        isPaidProduct
                          ? 'checkout.elearning_intro'
                          : 'checkout.elearning_intro_free',
                      )}
                  </p>
                  <ProductOverviewSection
                    productTitle={productTitle}
                    productType={productType}
                  />
                  <PersonalInformationSection
                    customerEmail={customerEmail}
                    displayMode={isPaidProduct ? 'form' : 'readonly'}
                    disableNameFields
                  />
                  <div ref={blockedBannerRef}>
                    {accessResult?.isBlocked && isPaidElearning && (
                      <ProductAccessBlockedBanner
                        productType={accessResult.productType}
                        reason={accessResult.reason}
                        variant="inline"
                        onOrderForSomeoneElse={() => {
                          resetResult();
                          // Set field value imperatively since form is already mounted
                          formRef.current?.setFieldValue(
                            'order_someone_else',
                            true,
                          );
                        }}
                      />
                    )}
                  </div>
                  {isPaidProduct &&
                    !hasMultipleProductsInCart &&
                    isElearningProduct && <OrderForSomeOneElseSection />}
                  {(Number(cartProductQuantity) >= 2 ||
                    formikProps.values.order_someone_else) && (
                    <ParticipantsBanner />
                  )}
                  {isPaidProduct && (
                    <BillingAddressSection
                      values={formikProps.values}
                      formRef={formRef}
                      selectAddress={selectAddress}
                      selectedBillingAddressIndex={selectedBillingAddressIndex}
                      setSelectedBillingAddressIndex={
                        setSelectedBillingAddressIndex
                      }
                      addressesRaw={addressesRaw}
                      defaultBillingIndex={defaultBillingIndex}
                    />
                  )}
                </div>

                <div className="Cart_summary">
                  <div className="CartSummary CartSummary__v2">
                    <div className="CartSummary__wrapper">
                      {isDigitalProduct && (
                        <div className="CartSummary__title">
                          <strong>
                            {productType === 'e_learning'
                              ? t('cart.e_learning')
                              : t('cart.digital_tool')}
                          </strong>
                        </div>
                      )}
                      <CartSummaryV2 showProducts />
                      <DiscountCodeSection
                        forSomeoneElse={formikProps.values.order_someone_else}
                      />
                    </div>

                    {/* Terms and Conditions */}
                    <p className="CartSummary__termsAndConditions">
                      {t('checkout.agree_terms_and_conditions')}{' '}
                      <a href="/algemene-voorwaarden">
                        {t('checkout.terms_and_conditions_link')}
                      </a>
                    </p>

                    <Button
                      type="submit"
                      className={`Button Button--primary ${
                        isLoading || isChecking ? 'Button--loading' : ''
                      }`}
                      disabled={
                        formikProps.isSubmitting ||
                        isLoading ||
                        isChecking ||
                        (accessResult?.isBlocked ?? false)
                      }
                    >
                      {productType === 'digital_tool'
                        ? t('cart.checkout')
                        : t('cart.place_order_elearning')}{' '}
                      <span className="icon icon-arrow-right icon-variant-2" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CustomFormikWrapper>
        )}
      </Container>
    </>
  );
};

export default CheckoutPageV2;
